import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { apiDelete, apiGet, apiPatch, apiPost } from '../lib/api'
import { useTenantId } from '../context/SessionContext'
import { useToast } from '../context/ToastContext'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { Button, FormField, Input, Modal, PageHeader, Skeleton } from '../components/ui'

const VALUE_PAGE_SIZE = 6
// All-tenant tag values fit comfortably under MaxTagsPerTenant (1000) — one
// fetch drives every key's value list and count, avoiding a fetch per key.
const ALL_TAGS_LIMIT = 1000
const MAX_CUSTOM_KEYS = 4

// System keys always render in this fixed order, not alphabetically.
const SYSTEM_KEY_ORDER = ['sensitivity', 'owner', 'criticality', 'external-access', 'env', 'location']
const SYSTEM_KEY_LABELS: Record<string, string> = {
  sensitivity: 'Sensitivity',
  owner: 'Owner',
  criticality: 'Criticality',
  'external-access': 'External access',
  env: 'Environment',
  location: 'Location',
}
function displayName(k: TagKey) {
  return SYSTEM_KEY_LABELS[k.key] ?? k.key.charAt(0).toUpperCase() + k.key.slice(1)
}

interface TagKey {
  id: string
  key: string
  is_system: boolean
  enabled: boolean
  multi_value: boolean
  created_at: string
}

interface TagRow {
  id: string
  tag_key_id: string
  key: string
  value: string
  resource_count?: number
  dependent_group_count?: number
}

interface PagedResponse<T> {
  items: T[]
  page: number
  page_size: number
  total: number
}

export default function AdminTagsPage() {
  const tenantId = useTenantId()
  const { toast } = useToast()

  const [tagKeys, setTagKeys] = useState<TagKey[]>([])
  const [allTags, setAllTags] = useState<TagRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [openKeys, setOpenKeys] = useState<Record<string, boolean>>({})
  const [addingFor, setAddingFor] = useState<string | null>(null)
  const [addDraft, setAddDraft] = useState('')
  const [saving, setSaving] = useState(false)

  const [valueFilter, setValueFilter] = useState<Record<string, string>>({})
  const [valuePage, setValuePage] = useState<Record<string, number>>({})

  const [showNewKey, setShowNewKey] = useState(false)
  const [newKeyName, setNewKeyName] = useState('')
  const [newKeyError, setNewKeyError] = useState<string | null>(null)
  const [creatingKey, setCreatingKey] = useState(false)

  const [deleteTarget, setDeleteTarget] = useState<TagRow | null>(null)
  const [deleting, setDeleting] = useState(false)

  const [deleteKeyTarget, setDeleteKeyTarget] = useState<TagKey | null>(null)
  const [deletingKey, setDeletingKey] = useState(false)

  const customKeyCount = useMemo(() => tagKeys.filter((k) => !k.is_system).length, [tagKeys])
  const sortedKeys = useMemo(() => {
    const system = tagKeys
      .filter((k) => k.is_system)
      .sort((a, b) => SYSTEM_KEY_ORDER.indexOf(a.key) - SYSTEM_KEY_ORDER.indexOf(b.key))
    const custom = tagKeys
      .filter((k) => !k.is_system)
      .sort((a, b) => a.created_at.localeCompare(b.created_at))
    return [...system, ...custom]
  }, [tagKeys])

  const valuesByKey = useMemo(() => {
    const map = new Map<string, TagRow[]>()
    for (const t of allTags) {
      const list = map.get(t.tag_key_id) ?? []
      list.push(t)
      map.set(t.tag_key_id, list)
    }
    for (const list of map.values()) list.sort((a, b) => a.value.localeCompare(b.value))
    return map
  }, [allTags])

  const loadKeys = useCallback(() => {
    if (!tenantId) return Promise.resolve()
    return apiGet<TagKey[]>(`/api/v1/tenants/${tenantId}/tag-keys`)
      .then((d) => setTagKeys(d ?? []))
      .catch((e: Error) => setError(e.message))
  }, [tenantId])

  const loadTags = useCallback(() => {
    if (!tenantId) return Promise.resolve()
    setLoading(true)
    setError(null)
    return apiGet<PagedResponse<TagRow>>(`/api/v1/tenants/${tenantId}/tags?limit=${ALL_TAGS_LIMIT}&offset=0`)
      .then((d) => setAllTags(d.items ?? []))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false))
  }, [tenantId])

  useEffect(() => { loadKeys() }, [loadKeys])
  useEffect(() => { loadTags() }, [loadTags])

  const toggleOpen = (keyId: string) => {
    setOpenKeys((prev) => ({ ...prev, [keyId]: !prev[keyId] }))
  }

  const startAdd = (keyId: string) => {
    setOpenKeys((prev) => ({ ...prev, [keyId]: true }))
    setAddingFor(keyId)
    setAddDraft('')
  }

  const commitAdd = async (key: TagKey) => {
    const value = addDraft.trim()
    if (!value) return
    setSaving(true)
    try {
      // Keys start disabled only for env/location; never surface an enabled
      // toggle in this UI — adding a first value silently activates the key.
      if (!key.enabled) {
        await apiPatch(`/api/v1/tenants/${tenantId}/tag-keys/${key.id}`, { enabled: true })
      }
      await apiPost(`/api/v1/tenants/${tenantId}/tags`, { tag_key_id: key.id, value })
      setAddingFor(null)
      setAddDraft('')
      await Promise.all([loadTags(), loadKeys()])
      toast({ title: `Added "${value}" to ${key.key}`, variant: 'success' })
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Create failed'
      toast({ title: 'Create failed', description: msg, variant: 'danger' })
    } finally {
      setSaving(false)
    }
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await apiDelete(`/api/v1/tenants/${tenantId}/tags/${deleteTarget.id}`)
      setDeleteTarget(null)
      loadTags()
      toast({ title: 'Value deleted', variant: 'success' })
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Delete failed'
      toast({ title: 'Delete failed', description: msg, variant: 'danger' })
    } finally {
      setDeleting(false)
    }
  }

  const confirmDeleteKey = async () => {
    if (!deleteKeyTarget) return
    setDeletingKey(true)
    try {
      await apiDelete(`/api/v1/tenants/${tenantId}/tag-keys/${deleteKeyTarget.id}`)
      setDeleteKeyTarget(null)
      await Promise.all([loadKeys(), loadTags()])
      toast({ title: `Deleted key "${deleteKeyTarget.key}"`, variant: 'success' })
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Delete failed'
      toast({ title: 'Delete failed', description: msg, variant: 'danger' })
    } finally {
      setDeletingKey(false)
    }
  }

  const openNewKeyModal = () => {
    setNewKeyName('')
    setNewKeyError(null)
    setShowNewKey(true)
  }

  const handleCreateKey = async (e: React.FormEvent) => {
    e.preventDefault()
    const name = newKeyName.trim()
    if (!name) return
    setCreatingKey(true)
    setNewKeyError(null)
    try {
      const created = await apiPost<TagKey>(`/api/v1/tenants/${tenantId}/tag-keys`, { key: name })
      setShowNewKey(false)
      await loadKeys()
      setOpenKeys((prev) => ({ ...prev, [created.id]: true }))
      toast({ title: `Created key "${name}"`, variant: 'success' })
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Create failed'
      setNewKeyError(msg)
    } finally {
      setCreatingKey(false)
    }
  }

  return (
    <div>
      <PageHeader
        title="Tags"
        description="Classify resources with governed tags, then target policies across many resources at once instead of one at a time."
      />

      {error && (
        <div className="mb-4 rounded-lg border border-danger/30 bg-danger-muted px-4 py-3 text-sm text-danger-foreground">
          {error}
        </div>
      )}

      <p className="mb-3 text-xs text-muted">
        {tagKeys.filter((k) => k.is_system).length} system keys, {customKeyCount} custom keys
      </p>

      <div className="mb-3 flex justify-end">
        <Button onClick={openNewKeyModal}>New key</Button>
      </div>

      {loading ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <div className="divide-y divide-border rounded-lg border border-border bg-surface">
          {sortedKeys.map((k) => {
            const values = valuesByKey.get(k.id) ?? []
            const isOpen = !!openKeys[k.id]
            const isEmpty = values.length === 0
            const isAdding = addingFor === k.id
            const resourceTotal = values.reduce((sum, v) => sum + (v.resource_count ?? 0), 0)
            const filterText = valueFilter[k.id] ?? ''
            const filtered = filterText.trim()
              ? values.filter((v) => v.value.toLowerCase().includes(filterText.trim().toLowerCase()))
              : values
            const page = valuePage[k.id] ?? 0
            const pageCount = Math.max(1, Math.ceil(filtered.length / VALUE_PAGE_SIZE))
            const clampedPage = Math.min(page, pageCount - 1)
            const pageValues = filtered.slice(clampedPage * VALUE_PAGE_SIZE, clampedPage * VALUE_PAGE_SIZE + VALUE_PAGE_SIZE)

            return (
              <div key={k.id}>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleOpen(k.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleOpen(k.id) }
                  }}
                  aria-expanded={isOpen}
                  className="flex w-full cursor-pointer items-center gap-3 px-4 py-3.5 text-left hover:bg-surface-muted"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-foreground">{displayName(k)}</span>
                      <span className="rounded border border-border bg-surface-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted">
                        {k.is_system ? 'System' : 'Custom'}
                      </span>
                    </div>
                    <span className="block font-mono text-xs text-muted">{k.key}</span>
                  </div>
                  {!k.is_system && (
                    <button
                      type="button"
                      className="shrink-0 text-xs text-danger hover:underline"
                      onClick={(e) => { e.stopPropagation(); setDeleteKeyTarget(k) }}
                    >
                      Delete
                    </button>
                  )}
                  <span className="shrink-0 text-xs text-muted">
                    {isEmpty ? (
                      'No values yet'
                    ) : (
                      <>
                        <b className="font-semibold text-foreground">{values.length}</b> values,{' '}
                        <b className="font-semibold text-foreground">{resourceTotal}</b> resources
                      </>
                    )}
                  </span>
                </div>

                {isOpen && (
                  <div className="border-t border-border px-4 pb-4 pt-1">
                    {isEmpty && !isAdding && (
                      <div className="py-4 text-center text-sm text-muted">
                        No values yet.{' '}
                        <button type="button" className="text-primary hover:underline" onClick={() => startAdd(k.id)}>
                          Add the first one
                        </button>
                      </div>
                    )}

                    {!isEmpty && !isAdding && (
                      <div className="flex justify-end pb-2 pt-2">
                        <Button variant="outline" size="sm" onClick={() => startAdd(k.id)}>Add value</Button>
                      </div>
                    )}

                    {isAdding && (
                      <div className="mb-2 flex items-center gap-2 rounded-lg border border-primary bg-primary/5 px-3 py-2">
                        <input
                          autoFocus
                          value={addDraft}
                          onChange={(e) => setAddDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') commitAdd(k)
                            if (e.key === 'Escape') { setAddingFor(null); setAddDraft('') }
                          }}
                          placeholder="New value name"
                          className="flex-1 bg-transparent text-sm outline-none"
                        />
                        <button type="button" className="text-sm text-primary hover:underline disabled:opacity-40" disabled={saving || !addDraft.trim()} onClick={() => commitAdd(k)}>
                          {saving ? 'Saving…' : 'Save'}
                        </button>
                        <button type="button" className="text-sm text-muted hover:underline" onClick={() => { setAddingFor(null); setAddDraft('') }}>
                          Cancel
                        </button>
                      </div>
                    )}

                    {values.length > VALUE_PAGE_SIZE && (
                      <input
                        value={filterText}
                        onChange={(e) => {
                          setValueFilter((prev) => ({ ...prev, [k.id]: e.target.value }))
                          setValuePage((prev) => ({ ...prev, [k.id]: 0 }))
                        }}
                        placeholder={`Filter ${values.length} values`}
                        className="mb-2 w-full rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs text-foreground outline-none focus:border-primary"
                      />
                    )}

                    {values.length > 0 && filtered.length === 0 && (
                      <div className="py-4 text-center text-xs text-muted">No values match "{filterText}"</div>
                    )}

                    {pageValues.length > 0 && (
                      <div className="overflow-hidden rounded-lg border border-border">
                        {pageValues.map((v) => (
                          <div
                            key={v.id}
                            className="group grid grid-cols-[1fr_150px_70px] items-center gap-2 bg-surface px-3 py-2.5"
                          >
                            <span className="text-sm">{v.value}</span>
                            {(v.resource_count ?? 0) > 0 ? (
                              <Link to={`/admin/resources?tag_id=${v.id}`} className="text-xs text-primary hover:underline">
                                {v.resource_count} {v.resource_count === 1 ? 'resource' : 'resources'}
                              </Link>
                            ) : (
                              <span className="text-xs text-muted">0 resources</span>
                            )}
                            <div className="flex justify-end opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
                              <button type="button" className="text-xs text-danger hover:underline" onClick={() => setDeleteTarget(v)}>
                                Delete
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {pageCount > 1 && (
                      <div className="mt-2 flex items-center justify-between px-1">
                        <span className="text-xs text-muted">
                          {clampedPage * VALUE_PAGE_SIZE + 1}-{Math.min(clampedPage * VALUE_PAGE_SIZE + VALUE_PAGE_SIZE, filtered.length)} of {filtered.length}
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            disabled={clampedPage === 0}
                            className="text-xs text-muted hover:underline disabled:opacity-30"
                            onClick={() => setValuePage((prev) => ({ ...prev, [k.id]: clampedPage - 1 }))}
                          >
                            Previous
                          </button>
                          <span className="text-xs text-muted">{clampedPage + 1} / {pageCount}</span>
                          <button
                            type="button"
                            disabled={clampedPage >= pageCount - 1}
                            className="text-xs text-muted hover:underline disabled:opacity-30"
                            onClick={() => setValuePage((prev) => ({ ...prev, [k.id]: clampedPage + 1 }))}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      <Modal
        open={showNewKey}
        onClose={() => setShowNewKey(false)}
        title="New custom key"
        description="Custom keys are open-ended, for example team or cost-center."
        footer={
          <>
            <Button variant="outline" onClick={() => setShowNewKey(false)}>Cancel</Button>
            <Button type="submit" form="new-key-form" disabled={creatingKey || !newKeyName.trim() || customKeyCount >= MAX_CUSTOM_KEYS}>
              {creatingKey ? 'Creating…' : 'Create'}
            </Button>
          </>
        }
      >
        <form id="new-key-form" onSubmit={handleCreateKey} className="space-y-3">
          {newKeyError && (
            <div className="rounded-lg border border-danger/30 bg-danger-muted px-3 py-2 text-sm text-danger-foreground">
              {newKeyError}
            </div>
          )}
          <FormField label="Key name" htmlFor="new-key-name">
            <Input id="new-key-name" value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)} placeholder="e.g. project" autoFocus />
          </FormField>
          <p className="text-xs text-muted">{customKeyCount} of {MAX_CUSTOM_KEYS} custom keys used.</p>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete value"
        message={deleteTarget ? `Delete '${deleteTarget.key}: ${deleteTarget.value}'? This removes the tag from ${deleteTarget.resource_count ?? 0} resources.` : ''}
        confirmLabel="Delete"
        destructive
        loading={deleting}
        onConfirm={confirmDelete}
        onClose={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={!!deleteKeyTarget}
        title="Delete key"
        message={(() => {
          if (!deleteKeyTarget) return ''
          const vals = valuesByKey.get(deleteKeyTarget.id) ?? []
          const resourceSum = vals.reduce((sum, v) => sum + (v.resource_count ?? 0), 0)
          return `Delete '${deleteKeyTarget.key}'? This removes ${vals.length} ${vals.length === 1 ? 'value' : 'values'} and untags ${resourceSum} ${resourceSum === 1 ? 'resource' : 'resources'}.`
        })()}
        confirmLabel="Delete"
        destructive
        loading={deletingKey}
        onConfirm={confirmDeleteKey}
        onClose={() => setDeleteKeyTarget(null)}
      />
    </div>
  )
}
